/**
 * Agent Lens Page
 *
 * Langfuse-inspired trace investigation surface.
 * Compact header with applet + element dropdowns,
 * 3-column layout (Insights | Trace Timeline | Node Detail),
 * full-width bottom workbench (Runs | Sandbox | Prompt Compare).
 *
 * Design follows the Executive Command Center page pattern.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Layout from '../../components/layout/Layout'
import {
  Page,
  PageBody,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelBody,
  Button,
  Badge,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Breadcrumb,
  BreadcrumbItem,
  Separator,
  Input,
  Textarea,
  Select,
  SelectOption,
  Tooltip,
} from '../../components/ui'
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../../components/ui/resizable-panel'
import {
  useTraces,
  useTraceDetail,
  useAppletMappings,
  usePromptVersions,
  useFeedbackSummary,
  useIterationReadiness,
  useSandboxRun,
} from '../../hooks/useAIConsole'
import type { TraceListItem, TraceObservation, AppletAgentMapping } from './types'
import {
  InformationCircleIcon,
  ChatBubbleLeftRightIcon,
  PlayIcon,
  ArrowTopRightOnSquareIcon,
  ChevronRightIcon,
  ClockIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline'
import { cn } from '../../shared/lib/cn'
import { APPLETS, fmtDuration, fmtCost } from './constants'
import { AIConsoleShell, type AIConsoleShellSummaryItem } from '../../components/ai-console/AIConsoleShell'
import {
  getMappingsForElement,
  getUniqueDataElements,
  resolveSelectedMapping,
} from './agentLensUtils'

// ── Helpers ───────────────────────────────────────────────────────────

interface TreeNode extends TraceObservation {
  children: TreeNode[]
  depth: number
  duration_ms?: number
}

function buildObservationTree(observations: TraceObservation[]): TreeNode[] {
  const nodeMap = new Map<string, TreeNode>()
  const roots: TreeNode[] = []

  for (const obs of observations) {
    const durationMs =
      obs.start_time && obs.end_time
        ? new Date(obs.end_time).getTime() - new Date(obs.start_time).getTime()
        : undefined
    nodeMap.set(obs.id, { ...obs, children: [], depth: 0, duration_ms: durationMs })
  }

  Array.from(nodeMap.values()).forEach(node => {
    if (node.parent_observation_id && nodeMap.has(node.parent_observation_id)) {
      const parent = nodeMap.get(node.parent_observation_id)!
      node.depth = parent.depth + 1
      parent.children.push(node)
    } else {
      roots.push(node)
    }
  })

  const flatten = (nodes: TreeNode[]): TreeNode[] => {
    const result: TreeNode[] = []
    for (const n of nodes.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))) {
      result.push(n)
      if (n.children.length) result.push(...flatten(n.children))
    }
    return result
  }

  return flatten(roots)
}


const OBS_TYPE_STYLES: Record<string, { dot: string; label: string; bg: string }> = {
  GENERATION: {
    dot: 'bg-purple-500',
    label: 'Generation',
    bg: 'bg-purple-50 dark:bg-purple-950/20 text-purple-700 dark:text-purple-300',
  },
  SPAN: {
    dot: 'bg-blue-500',
    label: 'Span',
    bg: 'bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-300',
  },
  EVENT: {
    dot: 'bg-amber-500',
    label: 'Event',
    bg: 'bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300',
  },
}

// ── Main Component ────────────────────────────────────────────────────

const AgentLensPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [selectedApplet, setSelectedApplet] = useState<string>(
    searchParams.get('applet') || 'chat'
  )
  const [selectedElement, setSelectedElement] = useState<string | undefined>(
    searchParams.get('element') || undefined
  )
  const [selectedAgent, setSelectedAgent] = useState<string | undefined>(
    searchParams.get('agent') || undefined
  )
  const [selectedTraceId, setSelectedTraceId] = useState<string>()
  const [selectedNodeId, setSelectedNodeId] = useState<string>()
  const [workbenchTab, setWorkbenchTab] = useState<'runs' | 'sandbox' | 'prompt-compare'>('runs')

  const { data: mappings } = useAppletMappings(selectedApplet)
  const { data: traces, isLoading: tracesLoading } = useTraces({
    applet_id: selectedApplet,
    data_element_key: selectedElement,
    agent_identifier: selectedAgent,
  })
  const { data: traceDetail } = useTraceDetail(selectedTraceId)
  const { data: feedbackSummary } = useFeedbackSummary({
    agent_identifier: selectedAgent,
    applet_id: selectedApplet,
  })
  const { data: readiness } = useIterationReadiness(selectedAgent)

  const dataElements = useMemo(() => getUniqueDataElements(mappings ?? []), [mappings])

  const elementMappings = useMemo(
    () => getMappingsForElement(mappings ?? [], selectedElement),
    [mappings, selectedElement]
  )

  const elementAgents = useMemo(() => {
    const unique = new Map<string, AppletAgentMapping>()
    for (const mapping of elementMappings) {
      const existing = unique.get(mapping.agent_identifier)
      if (!existing || (mapping.is_primary_producer && !existing.is_primary_producer)) {
        unique.set(mapping.agent_identifier, mapping)
      }
    }
    return Array.from(unique.values())
  }, [elementMappings])

  const selectedMapping = useMemo(
    () =>
      resolveSelectedMapping(mappings ?? [], {
        dataElementKey: selectedElement,
        agentIdentifier: selectedAgent,
      }),
    [mappings, selectedElement, selectedAgent]
  )
  const { data: promptVersions } = usePromptVersions(selectedMapping?.langfuse_prompt_key)

  const selectedPage = selectedMapping?.page_id

  const flatTree = useMemo(() => {
    if (!traceDetail?.observations) return []
    return buildObservationTree(traceDetail.observations)
  }, [traceDetail])

  const selectedObservation = useMemo(
    () => flatTree.find(o => o.id === selectedNodeId),
    [flatTree, selectedNodeId]
  )

  useEffect(() => {
    if (!mappings?.length) return

    const resolved = resolveSelectedMapping(mappings, {
      dataElementKey: selectedElement,
      agentIdentifier: selectedAgent,
    })
    if (!resolved) return

    if (!selectedElement && resolved.data_element_key) {
      setSelectedElement(resolved.data_element_key)
    }

    if (!selectedAgent || resolved.agent_identifier !== selectedAgent) {
      setSelectedAgent(resolved.agent_identifier)
    }
  }, [mappings, selectedElement, selectedAgent])

  useEffect(() => {
    if (!traces?.length) return

    const hasSelectedTrace = selectedTraceId && traces.some(trace => trace.trace_id === selectedTraceId)
    if (!hasSelectedTrace) {
      setSelectedTraceId(traces[0].trace_id)
      setSelectedNodeId(undefined)
    }
  }, [traces, selectedTraceId])

  useEffect(() => {
    if (!flatTree.length) return

    const hasSelectedNode = selectedNodeId && flatTree.some(node => node.id === selectedNodeId)
    if (!hasSelectedNode) {
      setSelectedNodeId(flatTree[0].id)
    }
  }, [flatTree, selectedNodeId])

  const handleAppletChange = useCallback((value: string) => {
    setSelectedApplet(value)
    setSelectedAgent(undefined)
    setSelectedElement(undefined)
    setSelectedTraceId(undefined)
    setSelectedNodeId(undefined)
  }, [])

  const handleElementChange = useCallback((value: string) => {
    const mapping = resolveSelectedMapping(mappings ?? [], { dataElementKey: value })
    if (mapping) {
      setSelectedElement(value)
      setSelectedAgent(mapping.agent_identifier)
      setSelectedTraceId(undefined)
      setSelectedNodeId(undefined)
    }
  }, [mappings])

  const handleAgentChange = useCallback((value: string) => {
    const mapping = resolveSelectedMapping(mappings ?? [], {
      dataElementKey: selectedElement,
      agentIdentifier: value,
    })
    if (mapping) {
      setSelectedElement(mapping.data_element_key)
      setSelectedAgent(mapping.agent_identifier)
      setSelectedTraceId(undefined)
      setSelectedNodeId(undefined)
    }
  }, [mappings, selectedElement])

  const appletLabel = APPLETS.find(a => a.id === selectedApplet)?.label ?? selectedApplet
  const selectedElementLabel = selectedMapping?.display_name || selectedElement || 'No element selected'
  const shellSummary: AIConsoleShellSummaryItem[] = [
    {
      label: 'Applet',
      value: appletLabel,
      detail: 'This is the product area you are investigating right now.',
    },
    {
      label: 'Current output',
      value: selectedElementLabel,
      detail: selectedElement
        ? 'This is the user-visible result you are trying to explain.'
        : 'Choose a mapped output to pull traces, prompts, and feedback into view.',
      tone: selectedElement ? ('brand' as const) : ('warning' as const),
    },
    {
      label: 'Recent runs',
      value: traces?.length ?? 0,
      detail: selectedElement
        ? 'Pick one run below to see the timeline, steps, and model output behind it.'
        : 'Recent runs will appear once you choose a mapped output.',
    },
    {
      label: 'Managed prompt',
      value: selectedMapping?.langfuse_prompt_key || 'Not linked yet',
      detail: selectedMapping?.langfuse_prompt_key
        ? `Lens found ${promptVersions?.length ?? 0} recorded version${promptVersions?.length === 1 ? '' : 's'} for this mapping.`
        : 'This output can still be inspected, but prompt history will stay empty until a managed prompt key is mapped.',
      tone: selectedMapping?.langfuse_prompt_key ? ('success' as const) : ('warning' as const),
    },
  ]

  return (
    <Layout>
      <Page layout="full-width">
        <PageBody fill padded={false}>
          <div className="px-8 pt-6">
            <AIConsoleShell
              sectionId="lens"
              title="Explain one result from end to end."
              description="Agent Lens is where you move from a symptom to an explanation. Choose one output, pick one run, then follow the exact chain of spans, generations, and prompts that produced it."
              plainLanguageSummary="Use this page when someone asks, 'Why did the assistant say that?' or 'Which step made this go wrong?' Start with a mapped output, open a run, and read the timeline from top to bottom."
              actions={
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      navigate(
                        `/ai-console/agent-testing?applet=${selectedApplet}${selectedAgent ? `&agent=${selectedAgent}` : ''}`,
                      )
                    }
                  >
                    Try a change
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setWorkbenchTab('prompt-compare')}>
                    Compare prompt versions
                  </Button>
                </div>
              }
              summaryItems={shellSummary}
            />
          </div>

          {/* ── Compact header bar ─────────────────────────────────── */}
          <div className="mt-6 px-8 pt-4 pb-3 flex items-center justify-between gap-4 border-b border-gray-200 dark:border-dark-border">
            <div className="flex items-center gap-4 min-w-0">
              <p className="text-sm font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400 shrink-0">
                Run focus
              </p>
              <Separator orientation="vertical" className="h-5" />
              <Breadcrumb>
                <BreadcrumbItem>{appletLabel}</BreadcrumbItem>
                {selectedPage && <BreadcrumbItem>{selectedPage}</BreadcrumbItem>}
                {selectedElement && (
                  <BreadcrumbItem isCurrent>
                    {selectedMapping?.display_name || selectedElement}
                  </BreadcrumbItem>
                )}
              </Breadcrumb>
            </div>

            {/* Trace summary badges (shown when a trace is selected) */}
            {traceDetail && (
              <div className="flex items-center gap-2 shrink-0">
                {traceDetail.latency != null && (
                  <Badge variant="outline" className="gap-1">
                    <ClockIcon className="w-3 h-3" />
                    {fmtDuration(traceDetail.latency)}
                  </Badge>
                )}
                {traceDetail.total_cost != null && (
                  <Badge variant="outline" className="gap-1">
                    <CurrencyDollarIcon className="w-3 h-3" />
                    {fmtCost(traceDetail.total_cost)}
                  </Badge>
                )}
                <Badge variant={traceDetail.status === 'ERROR' ? 'danger' : 'success'}>
                  {traceDetail.status || 'OK'}
                </Badge>
              </div>
            )}
          </div>

          {/* ── Selector row: Applet + Data Element dropdowns ───────── */}
          <div className="px-8 py-3 flex items-center gap-3 bg-gray-50/50 dark:bg-dark-surface/50 border-b border-gray-100 dark:border-dark-border/50">
            <Select
              value={selectedApplet}
              onValueChange={handleAppletChange}
              placeholder="Select applet"
            >
              {APPLETS.map(a => (
                <SelectOption key={a.id} value={a.id}>
                  {a.label}
                </SelectOption>
              ))}
            </Select>

            {dataElements.length > 0 && (
              <>
                <ChevronRightIcon className="w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0" />
                <Select
                  value={selectedElement || ''}
                  onValueChange={handleElementChange}
                  placeholder="Select data element"
                >
                  {dataElements.map(el => (
                    <SelectOption key={el.data_element_key} value={el.data_element_key}>
                      {el.display_name || el.data_element_key}
                    </SelectOption>
                  ))}
                </Select>
              </>
            )}

            {elementAgents.length > 0 && (
              <>
                <ChevronRightIcon className="w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0" />
                <Select
                  value={selectedAgent || ''}
                  onValueChange={handleAgentChange}
                  placeholder="Select agent"
                >
                  {elementAgents.map(mapping => (
                    <SelectOption
                      key={`${mapping.data_element_key}-${mapping.agent_identifier}`}
                      value={mapping.agent_identifier}
                    >
                      {mapping.agent_identifier}
                    </SelectOption>
                  ))}
                </Select>
              </>
            )}
          </div>

          <div className="px-8 py-4 border-b border-gray-100 dark:border-dark-border/50 bg-white dark:bg-dark-surface">
            <div className="grid grid-cols-1 xl:grid-cols-[1.1fr,0.95fr,0.85fr] gap-4">
              <Panel>
                <PanelHeader>
                  <PanelTitle className="text-sm">What You&apos;re Looking At</PanelTitle>
                </PanelHeader>
                <PanelBody>
                  <div className="grid grid-cols-2 gap-3">
                    <SignalTile
                      label="Applet"
                      value={appletLabel}
                      hint="Top-level product area currently in view."
                    />
                    <SignalTile
                      label="Page"
                      value={selectedPage || 'Unmapped'}
                      hint="The mapped page for the selected output."
                    />
                    <SignalTile
                      label="Element"
                      value={selectedElementLabel}
                      hint="The user-visible output you are tracing."
                    />
                    <SignalTile
                      label="Agent"
                      value={selectedAgent || 'Choose one'}
                      hint={`${elementAgents.length || 0} mapped agent${elementAgents.length === 1 ? '' : 's'} for this element.`}
                    />
                  </div>
                </PanelBody>
              </Panel>

              <Panel>
                <PanelHeader>
                  <PanelTitle className="text-sm">Managed Prompt</PanelTitle>
                </PanelHeader>
                <PanelBody>
                  <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-4 dark:border-dark-border/50 dark:bg-dark-surface-2/60">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                      Linked Prompt
                    </p>
                    <p className="mt-2 text-sm font-medium text-charcoal dark:text-gray-100 break-all">
                      {selectedMapping?.langfuse_prompt_key || 'No linked prompt'}
                    </p>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                      {selectedMapping?.langfuse_prompt_key
                        ? `Found ${promptVersions?.length ?? 0} version${promptVersions?.length === 1 ? '' : 's'} for this mapping.`
                        : 'This mapping does not currently expose a managed prompt key, so Lens can show traces but not prompt history.'}
                    </p>
                  </div>
                </PanelBody>
              </Panel>

              <Panel>
                <PanelHeader>
                  <PanelTitle className="text-sm">Continue From Here</PanelTitle>
                </PanelHeader>
                <PanelBody className="space-y-3">
                  <Button
                    className="w-full justify-between"
                    onClick={() =>
                      navigate(
                        `/ai-console/agent-testing?applet=${selectedApplet}${selectedAgent ? `&agent=${selectedAgent}` : ''}`,
                      )
                    }
                  >
                    Open Testing Hub
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full justify-between"
                    onClick={() => setWorkbenchTab('prompt-compare')}
                    disabled={!selectedMapping?.langfuse_prompt_key}
                  >
                    Compare Prompt Versions
                    <ChatBubbleLeftRightIcon className="h-4 w-4" />
                  </Button>
                </PanelBody>
              </Panel>
            </div>
          </div>

          {/* ── 3-column investigation layout ─────────────────────── */}
          <div className="px-8 py-4 min-h-[420px]">
            <ResizablePanelGroup
              direction="horizontal"
              className="min-h-[420px] rounded-xl border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface"
            >
              <ResizablePanel defaultSize={24} minSize={20}>
                <div className="h-full overflow-y-auto p-4">
                  <InsightsColumn
                    selectedApplet={selectedApplet}
                    selectedElement={selectedElement}
                    selectedMapping={selectedMapping}
                    feedbackSummary={feedbackSummary}
                    readiness={readiness}
                    traceCount={traces?.length ?? 0}
                    promptVersionCount={promptVersions?.length ?? 0}
                    onOpenSandbox={() => setWorkbenchTab('sandbox')}
                    onOpenPromptCompare={() => setWorkbenchTab('prompt-compare')}
                    onOpenFormalEvals={() => navigate('/evals')}
                  />
                </div>
              </ResizablePanel>
              <ResizableHandle />
              <ResizablePanel defaultSize={32} minSize={24}>
                <div className="h-full overflow-hidden p-4">
                  <TraceTimelineColumn
                    flatTree={flatTree}
                    selectedNodeId={selectedNodeId}
                    onNodeSelect={setSelectedNodeId}
                    hasTrace={!!selectedTraceId}
                    traceName={traceDetail?.name}
                    selectedAgent={selectedAgent}
                  />
                </div>
              </ResizablePanel>
              <ResizableHandle />
              <ResizablePanel defaultSize={44} minSize={28}>
                <div className="h-full overflow-y-auto p-4">
                  <NodeDetailColumn
                    observation={selectedObservation}
                    feedbackSummary={feedbackSummary}
                  />
                </div>
              </ResizablePanel>
            </ResizablePanelGroup>
          </div>

          {/* ── Bottom workbench ──────────────────────────────────── */}
          <div className="mt-2 border-t border-gray-200 dark:border-dark-border">
            <div className="px-8 pt-6 pb-8">
              <Tabs
                defaultValue="runs"
                value={workbenchTab}
                onValueChange={v => setWorkbenchTab(v as typeof workbenchTab)}
              >
                <TabsList>
                  <TabsTrigger value="runs">Recent Runs</TabsTrigger>
                  <TabsTrigger value="sandbox">Try a Change</TabsTrigger>
                  <TabsTrigger value="prompt-compare">Compare Versions</TabsTrigger>
                </TabsList>

                <TabsContent value="runs">
                  <RunsTab
                    traces={traces}
                    tracesLoading={tracesLoading}
                    selectedTraceId={selectedTraceId}
                    onSelectTrace={id => {
                      setSelectedTraceId(id)
                      setSelectedNodeId(undefined)
                    }}
                  />
                </TabsContent>

                <TabsContent value="sandbox">
                  <AgentSandboxTab
                    selectedApplet={selectedApplet}
                    selectedAgent={selectedAgent}
                    selectedMapping={selectedMapping}
                  />
                </TabsContent>

                <TabsContent value="prompt-compare">
                  <PromptCompareTab
                    agentIdentifier={selectedAgent}
                    promptKey={selectedMapping?.langfuse_prompt_key}
                  />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </PageBody>
      </Page>
    </Layout>
  )
}

// ── Left Column: Insights ─────────────────────────────────────────────

const InsightsColumn: React.FC<{
  selectedApplet: string
  selectedElement?: string
  selectedMapping?: AppletAgentMapping
  feedbackSummary?: { total: number; breakdown: Record<string, number>; period_days: number }
  readiness?: { score: number; ready: boolean; reason: string }
  traceCount: number
  promptVersionCount: number
  onOpenSandbox: () => void
  onOpenPromptCompare: () => void
  onOpenFormalEvals: () => void
}> = ({
  selectedApplet,
  selectedElement,
  selectedMapping,
  feedbackSummary,
  readiness,
  traceCount,
  promptVersionCount,
  onOpenSandbox,
  onOpenPromptCompare,
  onOpenFormalEvals,
}) => {
  const [changeInput, setChangeInput] = useState('')
  const feedbackBreakdown = feedbackSummary?.breakdown ?? {}
  const topFeedbackSignal = Object.entries(feedbackBreakdown).sort((a, b) => b[1] - a[1])[0]
  const nextAction = !selectedElement
    ? 'Choose a mapped element to see trace evidence and feedback.'
    : traceCount === 0
      ? 'Capture a fresh run in Sandbox so Agent Lens has evidence to inspect.'
      : readiness?.ready
        ? 'You have enough signal to compare prompt versions or move to a formal evaluation.'
        : 'You have some evidence, but not enough signal yet. Gather another run or more feedback.'

  if (!selectedElement) {
    return (
      <Panel className="flex-1">
        <PanelBody>
          <div className="flex flex-col items-center justify-center h-full py-12 text-center">
            <p className="text-sm font-medium text-charcoal dark:text-gray-100">
              Choose a mapped output to begin.
            </p>
            <p className="mt-2 max-w-xs text-sm text-gray-500 dark:text-gray-400">
              Once you select the output you care about, Agent Lens will pull in runs, prompt history, and feedback signals for that exact result.
            </p>
          </div>
        </PanelBody>
      </Panel>
    )
  }

  return (
    <Panel className="flex-1">
      <PanelHeader>
        <PanelTitle className="text-sm">Investigation Brief</PanelTitle>
      </PanelHeader>
      <PanelBody>
        <div className="space-y-4">
          <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-4 dark:border-dark-border/50 dark:bg-dark-surface-2/60">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
                  Current Target
                </p>
                <p className="mt-1 text-sm font-medium text-charcoal dark:text-gray-100">
                  {selectedMapping?.display_name || selectedElement}
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {selectedApplet} / {selectedMapping?.page_id || 'unmapped page'} / {selectedMapping?.agent_identifier || 'unmapped agent'}
                </p>
              </div>
              <Badge variant="outline">{traceCount} runs</Badge>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <SignalTile
              label="Readiness"
              value={readiness ? `${Math.round(readiness.score * 100)}%` : '—'}
              hint={readiness?.ready ? 'Ready to iterate' : 'Need more signal'}
            />
            <SignalTile
              label="Prompt Versions"
              value={promptVersionCount}
              hint={selectedMapping?.langfuse_prompt_key || 'No linked prompt'}
            />
            <SignalTile
              label="Feedback"
              value={feedbackSummary?.total ?? 0}
              hint={
                topFeedbackSignal
                  ? `${topFeedbackSignal[0].replace(/_/g, ' ')} leads`
                  : 'No user signal yet'
              }
            />
            <SignalTile
              label="Trace Strategy"
              value={selectedMapping?.trace_strategy || '—'}
              hint={selectedMapping?.flow_identifier || 'No flow mapping'}
            />
          </div>

          <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-4 dark:border-amber-800/40 dark:bg-amber-950/10">
            <div className="flex items-start gap-3">
              <InformationCircleIcon className="mt-0.5 h-4 w-4 text-amber-700 dark:text-amber-300" />
              <div>
                <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                  Recommended next move
                </p>
                <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
                  {nextAction}
                </p>
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-charcoal dark:text-gray-100">
                Change brief
              </label>
              <Tooltip content="Capture the change you want before you jump into sandbox or prompt comparison.">
                <InformationCircleIcon className="w-4 h-4 text-gray-400 dark:text-gray-500 cursor-help" />
              </Tooltip>
            </div>
            <Input
              placeholder="e.g. tighten citations and reduce repetitive phrasing"
              value={changeInput}
              onChange={e => setChangeInput(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 gap-2">
            <Button onClick={onOpenSandbox} className="justify-between">
              Open sandbox
              <PlayIcon className="h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={onOpenPromptCompare} className="justify-between">
              Compare prompt versions
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={onOpenFormalEvals} className="justify-between">
              Open formal evaluations
              <ChatBubbleLeftRightIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </PanelBody>
    </Panel>
  )
}

const SignalTile: React.FC<{ label: string; value: string | number; hint: string }> = ({ label, value, hint }) => (
  <div className="rounded-xl border border-gray-200 p-3 dark:border-dark-border/50">
    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
      {label}
    </p>
    <p className="mt-1 text-base font-semibold text-charcoal break-words dark:text-gray-100">
      {value}
    </p>
    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
      {hint}
    </p>
  </div>
)

// ── Center Column: Trace Timeline ─────────────────────────────────────

const TraceTimelineColumn: React.FC<{
  flatTree: TreeNode[]
  selectedNodeId?: string
  onNodeSelect: (id: string) => void
  hasTrace: boolean
  traceName?: string
  selectedAgent?: string
}> = ({ flatTree, selectedNodeId, onNodeSelect, hasTrace, traceName, selectedAgent }) => {
  if (!hasTrace) {
    return (
      <Panel className="flex-1">
        <PanelBody className="h-full">
          <div className="flex flex-col items-center justify-center h-full py-12 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {selectedAgent
                ? 'Choose one recent run below to open its trace.'
                : 'Choose a mapped output first, then pick a recent run.'}
            </p>
          </div>
        </PanelBody>
      </Panel>
    )
  }

  return (
    <Panel className="flex-1 overflow-hidden">
      <PanelHeader className="flex items-center justify-between">
        <PanelTitle className="text-sm">
          {traceName || 'Trace Timeline'}
        </PanelTitle>
        <Badge variant="info">{flatTree.length} spans</Badge>
      </PanelHeader>
      <PanelBody className="p-0 overflow-y-auto flex-1">
        {flatTree.length > 0 ? (
          <div className="py-1">
            {flatTree.map((node) => {
              const style = OBS_TYPE_STYLES[node.type || ''] || {
                dot: 'bg-gray-400',
                label: node.type || 'Unknown',
                bg: 'bg-gray-50 dark:bg-gray-800/30 text-gray-600 dark:text-gray-400',
              }
              const isSelected = selectedNodeId === node.id
              return (
                <button
                  key={node.id}
                  onClick={() => onNodeSelect(node.id)}
                  className={cn(
                    'w-full text-left px-4 py-2 transition-colors flex items-center gap-2',
                    'border-l-2',
                    isSelected
                      ? 'bg-gray-100 dark:bg-dark-surface-2 border-l-eliza-red'
                      : 'border-l-transparent hover:bg-gray-50 dark:hover:bg-dark-surface-2/50',
                  )}
                  style={{ paddingLeft: `${16 + node.depth * 20}px` }}
                >
                  {/* Depth connector dot */}
                  <span className={cn('w-2 h-2 rounded-full shrink-0', style.dot)} />

                  {/* Name + type */}
                  <div className="flex-1 min-w-0 flex items-center gap-2">
                    <span className={cn(
                      'text-sm truncate',
                      isSelected
                        ? 'font-semibold text-charcoal dark:text-gray-100'
                        : 'font-medium text-charcoal dark:text-gray-100',
                    )}>
                      {node.name || node.id.slice(0, 12)}
                    </span>
                  </div>

                  {/* Duration + cost */}
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 shrink-0">
                    {node.model && (
                      <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-dark-surface-2">
                        {node.model}
                      </span>
                    )}
                    <span className="font-mono tabular-nums">
                      {fmtDuration(node.duration_ms)}
                    </span>
                  </div>
                </button>
              )
            })}
          </div>
        ) : (
          <div className="flex items-center justify-center h-32 text-sm text-gray-500 dark:text-gray-400">
            No observations in this trace
          </div>
        )}
      </PanelBody>
    </Panel>
  )
}

// ── Right Column: Node Detail ─────────────────────────────────────────

const NodeDetailColumn: React.FC<{
  observation?: TreeNode
  feedbackSummary?: { total: number; breakdown: Record<string, number> }
}> = ({ observation, feedbackSummary }) => {
  if (!observation) {
    return (
      <Panel className="flex-1">
        <PanelBody>
          <div className="flex flex-col items-center justify-center h-full py-12 text-center">
            <p className="text-sm font-medium text-charcoal dark:text-gray-100">
              Choose one step in the run.
            </p>
            <p className="mt-2 max-w-xs text-sm text-gray-500 dark:text-gray-400">
              The detail panel will show the inputs, outputs, model usage, and metadata for that exact step.
            </p>
          </div>
        </PanelBody>
      </Panel>
    )
  }

  const style = OBS_TYPE_STYLES[observation.type || ''] || {
    dot: 'bg-gray-400',
    label: observation.type || 'Unknown',
    bg: 'bg-gray-50 dark:bg-gray-800/30 text-gray-600 dark:text-gray-400',
  }

  const metadata = observation.metadata || {}
  const inputData = observation.input
  const outputData = observation.output

  return (
    <div className="flex flex-col gap-4 overflow-y-auto flex-1">
      {/* Header */}
      <Panel>
        <PanelHeader className="flex items-center justify-between">
          <PanelTitle className="text-sm truncate">{observation.name || 'Node Details'}</PanelTitle>
          <Badge className={cn('text-[10px]', style.bg)}>{style.label}</Badge>
        </PanelHeader>
        <PanelBody className="p-0">
          <div className="grid grid-cols-2 divide-x divide-gray-100 dark:divide-dark-border/30">
            <KVCell label="Duration" value={fmtDuration(observation.duration_ms)} />
            <KVCell label="Cost" value={fmtCost(observation.cost)} />
          </div>
          {observation.model && (
            <div className="border-t border-gray-100 dark:border-dark-border/30">
              <KVCell label="Model" value={observation.model} mono />
            </div>
          )}
          {observation.status_message && (
            <div className="border-t border-gray-100 dark:border-dark-border/30">
              <KVCell label="Status" value={observation.status_message} />
            </div>
          )}
        </PanelBody>
      </Panel>

      {/* Input */}
      {inputData != null && (
        <Panel>
          <PanelHeader>
            <PanelTitle className="text-sm">Input</PanelTitle>
          </PanelHeader>
          <PanelBody className="p-0">
            <DataTable data={inputData} />
          </PanelBody>
        </Panel>
      )}

      {/* Output */}
      {outputData != null && (
        <Panel>
          <PanelHeader>
            <PanelTitle className="text-sm">Output</PanelTitle>
          </PanelHeader>
          <PanelBody className="p-0">
            <DataTable data={outputData} />
          </PanelBody>
        </Panel>
      )}

      {/* Metadata */}
      {Object.keys(metadata).length > 0 && (
        <Panel>
          <PanelHeader>
            <PanelTitle className="text-sm">Metadata</PanelTitle>
          </PanelHeader>
          <PanelBody className="p-0">
            <DataTable data={metadata} />
          </PanelBody>
        </Panel>
      )}

      {/* Feedback */}
      {feedbackSummary && feedbackSummary.total > 0 && (
        <Panel>
          <PanelHeader>
            <PanelTitle className="text-sm">
              User Feedback
              <Badge variant="secondary" className="ml-2">{feedbackSummary.total}</Badge>
            </PanelTitle>
          </PanelHeader>
          <PanelBody className="p-0">
            {Object.entries(feedbackSummary.breakdown).map(([type, count], i, arr) => (
              <div
                key={type}
                className={cn(
                  'flex items-center justify-between px-4 py-2 text-sm',
                  i < arr.length - 1 && 'border-b border-gray-100 dark:border-dark-border/20',
                )}
              >
                <span className="text-gray-500 dark:text-gray-400 capitalize">{type}</span>
                <span className="font-medium text-charcoal dark:text-gray-100">{count}</span>
              </div>
            ))}
          </PanelBody>
        </Panel>
      )}
    </div>
  )
}

// ── Shared: KV Cell ───────────────────────────────────────────────────

const KVCell: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div className="px-4 py-2.5">
    <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-0.5">
      {label}
    </div>
    <div className={cn(
      'text-sm text-charcoal dark:text-gray-100',
      mono && 'font-mono',
    )}>
      {value}
    </div>
  </div>
)

// ── Shared: Data Table (key-value pairs like Langfuse) ────────────────

const DataTable: React.FC<{ data: unknown }> = ({ data }) => {
  if (typeof data === 'string') {
    return (
      <pre className="text-xs font-mono p-4 overflow-auto max-h-48 text-charcoal dark:text-gray-100 whitespace-pre-wrap">
        {data}
      </pre>
    )
  }

  if (typeof data !== 'object' || data === null) {
    return (
      <pre className="text-xs font-mono p-4 overflow-auto max-h-48 text-charcoal dark:text-gray-100">
        {JSON.stringify(data, null, 2)}
      </pre>
    )
  }

  const entries = Object.entries(data as Record<string, unknown>)
  if (entries.length === 0) return null

  return (
    <table className="w-full border-collapse">
      <thead>
        <tr>
          <th className="px-4 py-2 text-left text-[10px] font-semibold tracking-wider uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-dark-border/30 bg-gray-50 dark:bg-dark-surface-2/50 w-[140px]">
            Path
          </th>
          <th className="px-4 py-2 text-left text-[10px] font-semibold tracking-wider uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-dark-border/30 bg-gray-50 dark:bg-dark-surface-2/50">
            Value
          </th>
        </tr>
      </thead>
      <tbody>
        {entries.map(([key, val], i) => (
          <tr
            key={key}
            className={cn(
              i < entries.length - 1 && 'border-b border-gray-100 dark:border-dark-border/20',
            )}
          >
            <td className="px-4 py-2 text-xs font-mono text-gray-500 dark:text-gray-400 align-top whitespace-nowrap">
              {key}
            </td>
            <td className="px-4 py-2 text-sm text-charcoal dark:text-gray-100 align-top">
              {typeof val === 'object' && val !== null ? (
                <pre className="text-xs font-mono overflow-auto max-h-24 whitespace-pre-wrap">
                  {JSON.stringify(val, null, 2)}
                </pre>
              ) : val === null ? (
                <span className="text-gray-400 dark:text-gray-500 italic">null</span>
              ) : (
                <span className={typeof val === 'string' ? '' : 'font-mono'}>
                  {typeof val === 'string' ? `"${val}"` : String(val)}
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// ── Workbench Tab: Runs ───────────────────────────────────────────────

const RunsTab: React.FC<{
  traces?: TraceListItem[]
  tracesLoading: boolean
  selectedTraceId?: string
  onSelectTrace: (id: string) => void
}> = ({ traces, tracesLoading, selectedTraceId, onSelectTrace }) => {
  if (tracesLoading) {
    return (
      <div className="py-4 space-y-2">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} variant="rounded" height={48} />
        ))}
      </div>
    )
  }

  if (!traces?.length) {
    return (
      <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
        No runs found. Select an agent or data element and traces will appear here.
      </div>
    )
  }

  return (
    <div className="divide-y divide-gray-100 dark:divide-dark-border/50">
      {traces.map(trace => {
        const displayName = trace.name || trace.trace_id.slice(0, 12)
        const timestamp = trace.created_at
          ? new Date(trace.created_at).toLocaleString([], {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          : undefined

        return (
          <button
            key={trace.trace_id}
            onClick={() => onSelectTrace(trace.trace_id)}
            className={cn(
              'w-full text-left px-4 py-3 transition-colors flex items-center justify-between',
              selectedTraceId === trace.trace_id
                ? 'bg-gray-100 dark:bg-dark-surface-2'
                : 'hover:bg-gray-50 dark:hover:bg-dark-surface-2/50',
            )}
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-sm font-medium text-charcoal dark:text-gray-100 truncate">
                {displayName}
              </span>
              <Badge variant={trace.status === 'ERROR' ? 'danger' : 'secondary'}>
                {trace.status || 'ok'}
              </Badge>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 shrink-0">
              {trace.latency != null && (
                <span className="font-mono tabular-nums">{fmtDuration(trace.latency)}</span>
              )}
              {trace.total_cost != null && (
                <span className="font-mono tabular-nums">{fmtCost(trace.total_cost)}</span>
              )}
              {timestamp && <span>{timestamp}</span>}
            </div>
          </button>
        )
      })}
    </div>
  )
}

// ── Workbench Tab: Agent Sandbox ──────────────────────────────────────

const AgentSandboxTab: React.FC<{
  selectedApplet: string
  selectedAgent?: string
  selectedMapping?: AppletAgentMapping
}> = ({ selectedApplet, selectedAgent, selectedMapping }) => {
  const [promptText, setPromptText] = useState('')
  const [systemPrompt, setSystemPrompt] = useState('')
  const [modelId, setModelId] = useState('gpt-4o')
  const [suggestedIdx, setSuggestedIdx] = useState<number>()
  const { mutate: runSandbox, isPending, data: sandboxResult } = useSandboxRun()
  const { data: promptVersions } = usePromptVersions(selectedMapping?.langfuse_prompt_key)
  const linkedPrompt = promptVersions?.[0]

  if (!selectedAgent) {
    return (
      <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
        Select an agent to use the sandbox
      </div>
    )
  }

  const handleRun = () => {
    runSandbox({
      applet_id: selectedMapping?.applet_id || selectedApplet,
      page_id: selectedMapping?.page_id,
      data_element_key: selectedMapping?.data_element_key,
      agent_identifier: selectedAgent,
      flow_identifier: selectedMapping?.flow_identifier,
      prompt_text: promptText || undefined,
      system_prompt: systemPrompt || undefined,
      model_id: modelId,
      test_input: {},
    })
  }

  return (
    <div className="py-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-charcoal dark:text-gray-100">Prompt</span>
          <div className="flex items-center gap-1 ml-2">
            {[1, 2, 3].map(i => (
              <Button
                key={i}
                variant={suggestedIdx === i ? 'default' : 'ghost'}
                size="sm"
                className="text-xs"
                onClick={() => setSuggestedIdx(i)}
              >
                Suggested {i}
              </Button>
            ))}
          </div>
        </div>
        <button className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors flex items-center gap-1">
          Create New Experiment
          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          {linkedPrompt && (
            <div className="mb-3 rounded-lg border border-gray-200 p-3 dark:border-dark-border">
              <div className="mb-2 flex items-center gap-2">
                <span className="text-sm font-medium text-charcoal dark:text-gray-100">
                  Linked Prompt
                </span>
                <Badge variant="outline">v{linkedPrompt.version}</Badge>
              </div>
              <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-md bg-gray-50 p-3 text-xs font-mono text-charcoal dark:bg-dark-surface-2 dark:text-gray-100">
                {linkedPrompt.prompt || 'No prompt text'}
              </pre>
            </div>
          )}

          <Textarea
            placeholder="Enter your prompt text here..."
            value={promptText}
            onChange={e => setPromptText(e.target.value)}
            className="min-h-[200px] font-mono text-sm"
          />
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-charcoal dark:text-gray-100 mb-1.5">
              Model
            </label>
            <Select value={modelId} onValueChange={setModelId}>
              <SelectOption value="gpt-4o">gpt-4o</SelectOption>
              <SelectOption value="gpt-4o-mini">gpt-4o-mini</SelectOption>
              <SelectOption value="gpt-4.1">gpt-4.1</SelectOption>
              <SelectOption value="claude-sonnet-4-20250514">Claude Sonnet 4</SelectOption>
              <SelectOption value="claude-3-5-haiku-20241022">Claude Haiku 3.5</SelectOption>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-charcoal dark:text-gray-100 mb-1.5">
              System Prompt
            </label>
            <Textarea
              placeholder="Override the system prompt..."
              value={systemPrompt}
              onChange={e => setSystemPrompt(e.target.value)}
              className="min-h-[80px] text-sm"
            />
          </div>

          <Button onClick={handleRun} disabled={isPending} className="w-full">
            <PlayIcon className="w-4 h-4 mr-2" />
            {isPending ? 'Running…' : 'Run'}
          </Button>

          {sandboxResult && (
            <div className="mt-3 rounded-lg border border-gray-200 dark:border-dark-border p-3">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="secondary">{sandboxResult.model_id}</Badge>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {sandboxResult.latency_ms}ms
                </span>
                {sandboxResult.cost_usd != null && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    ${sandboxResult.cost_usd.toFixed(4)}
                  </span>
                )}
              </div>
              <pre className="text-xs font-mono bg-gray-50 dark:bg-dark-surface-2 p-3 rounded-md overflow-auto max-h-40 text-charcoal dark:text-gray-100">
                {typeof sandboxResult.output === 'string'
                  ? sandboxResult.output
                  : JSON.stringify(sandboxResult.output, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Workbench Tab: Prompt Version Compare ─────────────────────────────

const PromptCompareTab: React.FC<{
  agentIdentifier?: string
  promptKey?: string
}> = ({ agentIdentifier, promptKey }) => {
  const { data: versions, isLoading } = usePromptVersions(promptKey)

  if (!agentIdentifier) {
    return (
      <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
        Pick an agent
      </div>
    )
  }

  if (!promptKey) {
    return (
      <div className="py-8">
        <Panel>
          <PanelBody>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This agent ({agentIdentifier}) does not have a Langfuse prompt catalog key.
              Prompt comparison is available for agents with managed prompts.
            </p>
          </PanelBody>
        </Panel>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="py-4 space-y-4">
        <Skeleton variant="rounded" height={40} />
        <Skeleton variant="rounded" height={300} />
      </div>
    )
  }

  const hasVersions = versions && versions.length >= 2

  return (
    <div className="py-4">
      {hasVersions ? (
        <ResizablePanelGroup
          direction="horizontal"
          className="min-h-[300px] rounded-lg border border-gray-200 dark:border-dark-border"
        >
          <ResizablePanel defaultSize={50} minSize={30}>
            <div className="h-full p-4 overflow-auto">
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="outline">v{versions[1]?.version ?? '?'}</Badge>
                <span className="text-sm text-gray-500 dark:text-gray-400">Current Prompt</span>
              </div>
              <pre className="text-xs font-mono bg-gray-50 dark:bg-dark-surface-2 p-4 rounded-md whitespace-pre-wrap text-charcoal dark:text-gray-100">
                {versions[1]?.prompt || 'No prompt text'}
              </pre>
            </div>
          </ResizablePanel>

          <ResizableHandle />

          <ResizablePanel defaultSize={50} minSize={30}>
            <div className="h-full p-4 overflow-auto">
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="default">v{versions[0]?.version ?? '?'}</Badge>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Version {versions[0]?.version}
                  {versions[0]?.is_active && ' (Active)'}
                </span>
              </div>
              <pre className="text-xs font-mono bg-gray-50 dark:bg-dark-surface-2 p-4 rounded-md whitespace-pre-wrap text-charcoal dark:text-gray-100">
                {versions[0]?.prompt || 'No prompt text'}
              </pre>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      ) : versions?.[0] ? (
        <Panel>
          <PanelHeader>
            <PanelTitle>
              Prompt: {promptKey}
              <Badge variant="default" className="ml-2">v{versions[0].version}</Badge>
            </PanelTitle>
          </PanelHeader>
          <PanelBody>
            <pre className="text-xs font-mono bg-gray-50 dark:bg-dark-surface-2 p-4 rounded-md overflow-auto max-h-96 whitespace-pre-wrap text-charcoal dark:text-gray-100">
              {versions[0].prompt || 'No prompt text available'}
            </pre>
          </PanelBody>
        </Panel>
      ) : (
        <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
          No versions found for this prompt
        </div>
      )}
    </div>
  )
}

export default AgentLensPage
